import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { Criteria_Config, Milestone, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { getCriteriaConfigForMilestoneAssessmentQuery } from 'src/screens/shared/assessment-criteria/assessmentQueries'
import { ValidationListItemProps } from 'src/screens/shared/common/SummaryValidationUtils'
import { getKapMeasureAnnualPlanningValidation } from 'src/screens/shared/implementation/details/milestone/utils/AnnualPlanningValidationUtils'
import {
  getFeatureKpiValidation,
  getKapGoalRatingValidation,
  getKapMeasureRatingValidation,
  getProjectGoalMeasureRatingValidation,
} from 'src/screens/shared/implementation/details/milestone/utils/MilestoneAnnualReportValidation'
import {
  getProgramValidationForAnnualPlanning,
  getProgramValidationForAnnualReport,
  getProgramValidationForContract,
  getProgramValidationForFinalReport,
  getProgramValidationForFinancialReport,
  getProgramValidationForIntermediateDiscussion,
} from 'src/screens/shared/implementation/details/milestone/utils/ProgramMilestoneDueDateValidations'
import {
  getProjectValidationForAnnualReport,
  getProjectValidationForContract,
  getProjectValidationForFinalReport,
  getProjectValidationForFinancialReport,
  getProjectValidationForIntermediateDiscussion,
} from 'src/screens/shared/implementation/details/milestone/utils/ProjectMilestoneDueDateValidation'
import {
  fetchFinancialReportsByMilestoneIdQuery,
  fetchQualitativeEvaluationByMilestoneQuery,
  milestoneConclusionDataQuery,
  milestoneEvaluationQuery,
  milestoneNotesQuery,
  queryKapGoalsFinalReportRatingAndReason,
} from 'src/screens/shared/implementation/details/milestoneQueries'
import { fetchPrinciplesWithActiveSubprinciplesQuery } from 'src/screens/shared/principles/kapProgramPrinciplesQueries'
import { ASSESSMENT_TYPE, CRITERIA_TYPE } from 'src/shared/constants/assessment-constants'
import { ENTITY_TYPE, FILE_TYPE, PROCESS_TYPE, PROJECT, PROJECT_TYPE } from 'src/shared/constants/constants'
import {
  MILESTONE_STATUS,
  MILESTONE_STATUS_TYPE,
  MILESTONE_TYPE,
  MILESTONE_TYPE_TYPE,
} from 'src/shared/constants/milestone-constants'
import { queryFile } from 'src/shared/presentation/fileQueries'
import { DateUtils } from 'src/shared/utils/DateUtils'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { useUserLocale } from 'src/user/UserContext'
import { useClient } from 'urql'

// General milestone summary Validations

/**
 * Gets the general validations for summary page for milestones on Projects and Programs.
 *
 * @param milestoneId - The ID of the milestone to validate.
 * @param milestoneType - The type of the milestone
 * @param processType - The process type
 * @param milestoneStatus - (Optional) The current status of the milestone.
 * @param projectId - (Optional) The ID of the project associated with the milestone.
 * @param applicationTypeId - (Optional) The ID of the application type, if applicable.
 * @returns The list of validation items relevant to the milestone.
 */
export const useValidateMilestone = (
  milestoneId: number,
  milestoneType: MILESTONE_TYPE_TYPE,
  processType: PROJECT_TYPE,
  milestoneStatus?: MILESTONE_STATUS_TYPE | undefined,
  projectId?: number,
  applicationTypeId?: number,
): ValidationListItemProps[] => {
  const [validationViolations, setValidationViolations] = useState<ValidationListItemProps[]>([])
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const { programId } = useParams()
  const locale = useUserLocale()

  const getNotesValidation = useCallback(async () => {
    const violations: ValidationListItemProps[] = []

    const { data } = await urqlClient
      .query<{ milestone_by_pk: Query_Root['milestone_by_pk'] }>(milestoneNotesQuery, { milestoneId })
      .toPromise()

    if (data?.milestone_by_pk?.notes === null || data?.milestone_by_pk?.notes === '') {
      const violation = {
        message: getMessage('validation.fill.out.section', [
          getMessage('label.navigation.implementation.details'),
          getMessage('label.notes'),
        ]),
        tab: 'details',
        section: 'notes',
      }
      violations.push(violation)
    }
    return violations
  }, [getMessage, milestoneId, urqlClient])

  const getEvaluationNeedValidation = useCallback(async () => {
    const violations: ValidationListItemProps[] = []

    const { data } = await urqlClient
      .query<{ intermediate_discussion_evaluation: Query_Root['intermediate_discussion_evaluation'] }>(
        milestoneEvaluationQuery,
        { milestoneId },
      )
      .toPromise()

    if (
      data?.intermediate_discussion_evaluation.length === 0 ||
      data?.intermediate_discussion_evaluation?.[0]?.evaluation_need === null
    ) {
      const violation = {
        message: getMessage('validation.fill.out.section', [
          getMessage('label.navigation.implementation.evaluation'),
          getMessage('label.evaluation.need'),
        ]),
        tab: 'evaluation',
      }
      violations.push(violation)
    }
    return violations
  }, [getMessage, milestoneId, urqlClient])

  const getFileUploadValidation = useCallback(
    async (tabMessageLabel: string, sectionMessageLabel: string, tab: string) => {
      const violations: ValidationListItemProps[] = []
      const fileType = FILE_TYPE.MILESTONE_DETAILS_DOCUMENT
      const entityType = ENTITY_TYPE.MILESTONE
      const entityId = milestoneId

      const { data } = await urqlClient
        .query<
          { file: Query_Root['file'] },
          {
            fileType: string
            entityType: string
            entityId: number
          }
        >(queryFile, { fileType, entityType, entityId })
        .toPromise()

      if (data?.file.length === 0) {
        const violation = {
          message: getMessage('validation.section.missing.upload', [
            getMessage(tabMessageLabel),
            getMessage(sectionMessageLabel),
          ]),
          tab: tab,
          section: 'documents',
        }
        violations.push(violation)
      }
      return violations
    },
    [getMessage, milestoneId, urqlClient],
  )

  const getExternalAssessmentValidation = useCallback(async () => {
    const violations: ValidationListItemProps[] = []

    // This validation is only for PF-KAP.
    if (processType !== PROJECT.PF_KAP) {
      return violations
    }

    const { data } = await urqlClient
      .query<{
        milestone_assessment_criteria_selection: Query_Root['milestone_assessment_criteria_selection']
        criteria_group_sorting_config: Query_Root['criteria_group_sorting_config']
      }>(getCriteriaConfigForMilestoneAssessmentQuery, {
        milestoneId: milestoneId,
        process: processType,
        criteriaType: CRITERIA_TYPE.QUALITY,
        assessorType: ASSESSMENT_TYPE.EXTERNAL,
        applicationTypeId: applicationTypeId,
      })
      .toPromise()

    if (data) {
      const mergedCriteriaConfigs: Criteria_Config[] = data.criteria_group_sorting_config.flatMap((conf) =>
        conf.criteria_group_config.criteria_sorting_configs.flatMap((csc) => csc.criteria_config),
      )

      const criteriaConfigSelections = mergedCriteriaConfigs.map((it) => {
        const configId = it.id
        const selectionValue = data.milestone_assessment_criteria_selection.find(
          (it) => it.criteria_config_id === configId,
        )?.rating

        return {
          config_id: configId,
          selection: selectionValue,
        }
      })

      if (criteriaConfigSelections.some((it) => !it.selection)) {
        const violation = {
          message: getMessage('validation.fill.out.section', [
            getMessage('label.assessment.type.external_assessment'),
            getMessage('label.assessment.type.external_assessment'),
          ]),
          tab: 'external-assessment',
        }
        violations.push(violation)
      }
    }

    return violations
  }, [processType, getMessage, milestoneId, urqlClient, applicationTypeId])

  const getFinalReportConclusionValidation = useCallback(async () => {
    const violations: ValidationListItemProps[] = []

    // This validation is only for PF-KAP.
    if (processType !== PROJECT.PF_KAP) {
      return violations
    }

    const { data } = await urqlClient
      .query<{
        milestone_by_pk: Query_Root['milestone_by_pk']
      }>(milestoneConclusionDataQuery, {
        milestoneId: milestoneId,
      })
      .toPromise()

    // These validations are only applied before setting the milestone to DONE.
    // For already exising - completed milestones (status is DONE), show validations if Conclusion tab is incomplete
    if (
      data?.milestone_by_pk &&
      (data.milestone_by_pk.status === MILESTONE_STATUS.REVIEW || data.milestone_by_pk.status === MILESTONE_STATUS.DONE)
    ) {
      const milestone = data.milestone_by_pk

      if (!milestone.rating) {
        const violation = {
          message: getMessage('validation.fill.out.section', [
            getMessage('label.navigation.milestone.conclusion'),
            getMessage('label.milestone.conclusion.rating'),
          ]),
          tab: 'conclusion',
          section: 'rating',
        }
        violations.push(violation)
      }

      if (
        !milestone.status_after_support ||
        (Array.isArray(milestone.status_after_support) && milestone.status_after_support.length === 0)
      ) {
        const violation = {
          message: getMessage('validation.fill.out.section', [
            getMessage('label.navigation.milestone.conclusion'),
            getMessage('label.milestone.conclusion.status_after_support'),
          ]),
          tab: 'conclusion',
          section: 'status_after_support',
        }
        violations.push(violation)
      }

      if (
        !milestone.cantonal_support ||
        (Array.isArray(milestone.cantonal_support) && milestone.cantonal_support.length === 0)
      ) {
        const violation = {
          message: getMessage('validation.fill.out.section', [
            getMessage('label.navigation.milestone.conclusion'),
            getMessage('label.milestone.conclusion.cantonal_support'),
          ]),
          tab: 'conclusion',
          section: 'cantonal_support',
        }
        violations.push(violation)
      }

      if (!milestone.conclusion) {
        const violation = {
          message: getMessage('validation.fill.out.section', [
            getMessage('label.navigation.milestone.conclusion'),
            getMessage('label.milestone.conclusion'),
          ]),
          tab: 'conclusion',
          section: 'conclusion',
        }
        violations.push(violation)
      }
    }

    return violations
  }, [processType, getMessage, milestoneId, urqlClient])

  const getQualitativeEvaluationValidation = useCallback(async () => {
    const violations: ValidationListItemProps[] = []

    const { data } = await urqlClient
      .query<{ milestone_qualitative_evaluation: Query_Root['milestone_qualitative_evaluation'] }>(
        fetchQualitativeEvaluationByMilestoneQuery,
        { milestoneId },
      )
      .toPromise()

    if (processType === PROJECT.PF_KAP || processType === PROJECT.PF_PGV) {
      const qualitativeEvaluation = data?.milestone_qualitative_evaluation[0]

      if (
        !qualitativeEvaluation?.strength ||
        !qualitativeEvaluation?.weakness ||
        !qualitativeEvaluation?.opportunities ||
        !qualitativeEvaluation?.threats
      ) {
        const violation = {
          message: getMessage('validation.fill.out.section', [
            getMessage('label.navigation.milestone.qualitative.evaluation'),
            getMessage('label.milestone.qualitative.evaluation.swot.analysis'),
          ]),
          tab: 'qualitative-evaluation',
          section: 'swot-analysis',
        }
        violations.push(violation)
      }

      if (!qualitativeEvaluation?.equal_opportunity) {
        const violation = {
          message: getMessage('validation.fill.out.section', [
            getMessage('label.navigation.milestone.qualitative.evaluation'),
            getMessage('label.background.information.equal.opportunity'),
          ]),
          tab: 'qualitative-evaluation',
          section: 'equal-opportunity',
        }
        violations.push(violation)
      }

      if (!qualitativeEvaluation?.sustainability) {
        const violation = {
          message: getMessage('validation.fill.out.section', [
            getMessage('label.navigation.milestone.qualitative.evaluation'),
            getMessage('label.background.information.sustainability'),
          ]),
          tab: 'qualitative-evaluation',
          section: 'sustainability',
        }
        violations.push(violation)
      }

      if (!qualitativeEvaluation?.multiplication_potential) {
        const violation = {
          message: getMessage('validation.fill.out.section', [
            getMessage('label.navigation.milestone.qualitative.evaluation'),
            getMessage('label.background.information.multiplication.potential'),
          ]),
          tab: 'qualitative-evaluation',
          section: 'multiplication-potential',
        }
        violations.push(violation)
      }
    }

    return violations
  }, [processType, getMessage, milestoneId, urqlClient])

  const getFinancialsValidation = useCallback(async () => {
    const violations: ValidationListItemProps[] = []
    const isProgram = processType === PROJECT.KAP

    const { data } = await urqlClient
      .query<{
        project_financial_report: Query_Root['project_financial_report']
        kap_program_financial_report: Query_Root['kap_program_financial_report']
      }>(fetchFinancialReportsByMilestoneIdQuery, {
        milestoneId: milestoneId,
        isProgram: isProgram,
      })
      .toPromise()

    // PF KAP - PF PGV Projects violation
    const projectFinancialReport = data?.project_financial_report?.[0]
    const gfchShareField = projectFinancialReport?.gfch_share_annual_value ?? null
    const shareResponsibleOrganizationField =
      projectFinancialReport?.share_responsible_organization_annual_value ?? null
    const otherFundingField = projectFinancialReport?.other_funding_annual_value ?? null
    if (
      !isProgram &&
      (gfchShareField === null || shareResponsibleOrganizationField === null || otherFundingField === null)
    ) {
      const violation = {
        message: getMessage('validation.milestone.fill.out.financial.report'),
        tab: 'financials',
        section: 'milestone-project-financial-report',
      }
      violations.push(violation)
    }

    // KAP Programs violation
    const kapProgramFinancialReport = data?.kap_program_financial_report ?? []
    const hasAllGfchActualValues = kapProgramFinancialReport?.every(
      (moduleReport) => moduleReport.gfch_actual_value !== null,
    )
    const hasAllCantonActualValues = kapProgramFinancialReport?.every(
      (moduleReport) => moduleReport.canton_actual_value !== null,
    )
    if (isProgram && (kapProgramFinancialReport?.length < 1 || !hasAllGfchActualValues || !hasAllCantonActualValues)) {
      const violation = {
        message: getMessage('validation.milestone.fill.out.program.actuals'),
        tab: 'financials',
        section: 'program-actuals',
      }
      violations.push(violation)
    }

    return violations
  }, [getMessage, milestoneId, urqlClient, processType])

  const getPrinciplesValidation = useCallback(async () => {
    const violations: ValidationListItemProps[] = []

    const { data } = await urqlClient
      .query<{
        principle_config: Query_Root['principle_config']
      }>(fetchPrinciplesWithActiveSubprinciplesQuery, {
        locale: locale,
        programId: programId,
      })
      .toPromise()

    const principleConfigs = data?.principle_config
    const hasViolation = principleConfigs?.some(
      (pc) => pc.subprinciple_configs?.some((sc) => sc.kap_program_subprinciples?.[0]?.implementation_comment === null),
    )

    if (hasViolation) {
      const violation = {
        message: getMessage('validation.milestone.fill.out.principles'),
        tab: 'principles',
      }

      violations.push(violation)
    }

    return violations
  }, [getMessage, urqlClient, programId, locale])

  const getKapFinalGoalRatingValidation = useCallback(async (): Promise<ValidationListItemProps[]> => {
    const { data } = await urqlClient
      .query<{ kap_program: Query_Root['kap_program'] }>(queryKapGoalsFinalReportRatingAndReason, { milestoneId })
      .toPromise()

    const goals = data?.kap_program[0]?.kap_goals

    const goalsNotFilled = goals?.some((goal) => {
      const goalReport = goal.kap_goal_final_reports?.[0]
      return !goalReport?.rating || !goalReport.reason || HtmlSanitizer.stripHtmlTags(goalReport.reason).trim() === ''
    })

    if (goalsNotFilled) {
      return [
        {
          message: getMessage('validation.milestone.kap.final.report.fill.out.goals.rating'),
          tab: 'four-year-goal-rating',
        },
      ]
    }

    return []
  }, [getMessage, urqlClient, milestoneId])

  useEffect(() => {
    const validateMilestone = async () => {
      const violations: ValidationListItemProps[] = []
      switch (milestoneType) {
        case MILESTONE_TYPE.INTERMEDIATE_DISCUSSION: {
          if (processType === PROJECT.KAP) {
            const fileViolation = await getFileUploadValidation(
              'label.navigation.implementation.details',
              `label.${milestoneType}.documents`,
              'details',
            )
            violations.push(...fileViolation)
          } else if (processType === PROJECT.PF_KAP) {
            const [notesViolation, evaluationNeedValidations] = await Promise.all([
              getNotesValidation(),
              getEvaluationNeedValidation(),
            ])
            violations.push(...notesViolation, ...evaluationNeedValidations)
          } else if (processType === PROJECT.PF_PGV) {
            const notesViolation = await getNotesValidation()
            violations.push(...notesViolation)
          }
          break
        }
        case MILESTONE_TYPE.CONTRACT:
        case MILESTONE_TYPE.EVALUATION_CONCEPT: {
          const violations = await getFileUploadValidation(
            'label.navigation.implementation.details',
            `label.${milestoneType}.documents`,
            'details',
          )
          violations.push(...violations)
          break
        }
        case MILESTONE_TYPE.FINAL_REPORT: {
          if (processType === PROJECT.KAP) {
            const [principlesValidation, goalRatingValidation] = await Promise.all([
              getPrinciplesValidation(),
              getKapFinalGoalRatingValidation(),
            ])

            violations.push(...principlesValidation, ...goalRatingValidation)
          } else {
            const [
              getExternalAssessmentValidations,
              getQualitativeEvaluationValidations,
              getFinalReportConclusionValidations,
            ] = await Promise.all([
              getExternalAssessmentValidation(),
              getQualitativeEvaluationValidation(),
              getFinalReportConclusionValidation(),
            ])
            violations.push(
              ...getExternalAssessmentValidations,
              ...getQualitativeEvaluationValidations,
              ...getFinalReportConclusionValidations,
            )
          }
          break
        }
        case MILESTONE_TYPE.ANNUAL_REPORT: {
          if (processType === PROJECT.KAP) {
            const [getKapMeasureRatingViolations, getKapGoalRatingViolations] = await Promise.all([
              getKapMeasureRatingValidation({ milestoneId, getMessage, urqlClient }),
              getKapGoalRatingValidation({ milestoneId, getMessage, urqlClient }),
            ])
            violations.push(...getKapMeasureRatingViolations, ...getKapGoalRatingViolations)
            break
          }
          if (projectId) {
            const [goalMeasureRatingViolations, kpiViolations] = await Promise.all([
              getProjectGoalMeasureRatingValidation({ milestoneId, getMessage, urqlClient }),
              getFeatureKpiValidation({ milestoneId, getMessage, urqlClient, projectId, processType }),
            ])
            violations.push(...goalMeasureRatingViolations, ...kpiViolations)
          }
          break
        }
        case MILESTONE_TYPE.FINANCIAL_REPORT: {
          if (processType === PROJECT.KAP) {
            const financialsViolations = await getFinancialsValidation()

            violations.push(...financialsViolations)
          } else {
            const [financialsViolations, fileUploadViolations] = await Promise.all([
              getFinancialsValidation(),
              getFileUploadValidation(
                'label.navigation.project.financials',
                'label.milestone.detailed.financial.report',
                'financials',
              ),
            ])
            violations.push(...financialsViolations, ...fileUploadViolations)
          }
          break
        }
        case MILESTONE_TYPE.ANNUAL_PLAN: {
          const violations = await getKapMeasureAnnualPlanningValidation(milestoneId, urqlClient, getMessage)
          violations.push(...violations)
          break
        }
        default:
          throw Error(`Unsupported milestone type [${milestoneType}]`)
      }

      setValidationViolations(violations)
    }
    validateMilestone()
  }, [
    milestoneType,
    milestoneStatus,
    processType,
    getNotesValidation,
    getEvaluationNeedValidation,
    getFileUploadValidation,
    getExternalAssessmentValidation,
    getQualitativeEvaluationValidation,
    getFinancialsValidation,
    getPrinciplesValidation,
    getMessage,
    milestoneId,
    projectId,
    urqlClient,
    getFinalReportConclusionValidation,
    getKapFinalGoalRatingValidation,
  ])

  return validationViolations
}

// Due Date milestone Validations

export type ValidationForEditMilestoneDueDate = {
  message: string
  minDate?: Date
  maxDate?: Date
}

/**
 * Gets the validation date range for milestone due date on Projects and Programs.
 *
 * @param currentMilestone the current milestone for validation
 * @param milestones list of ordered milestones by year, task date, due date
 * @param processType the process where this milestone belongs.
 * @returns the validation
 */
export const getValidationDateRangeForMilestoneDueDate = (
  currentMilestone: Milestone,
  milestones: Milestone[],
  processType: PROCESS_TYPE,
): ValidationForEditMilestoneDueDate | undefined => {
  const milestoneType = currentMilestone.type as MILESTONE_TYPE_TYPE

  const minContractDate = getDefaultMinAllowedDateForMilestone(milestones)
  const maxFinalReportDate = getDefaultMaxAllowedDateForMilestone(milestones)

  switch (milestoneType) {
    case MILESTONE_TYPE.CONTRACT:
      if (processType === PROJECT.KAP) {
        return getProgramValidationForContract(milestones)
      } else {
        return getProjectValidationForContract(milestones)
      }

    case MILESTONE_TYPE.INTERMEDIATE_DISCUSSION:
      if (processType === PROJECT.KAP) {
        return getProgramValidationForIntermediateDiscussion(
          currentMilestone,
          milestones,
          minContractDate,
          maxFinalReportDate,
        )
      } else {
        return getProjectValidationForIntermediateDiscussion(
          currentMilestone,
          milestones,
          minContractDate,
          maxFinalReportDate,
        )
      }

    case MILESTONE_TYPE.ANNUAL_REPORT:
      if (processType === PROJECT.KAP) {
        return getProgramValidationForAnnualReport(currentMilestone, milestones, minContractDate, maxFinalReportDate)
      } else {
        return getProjectValidationForAnnualReport(currentMilestone, milestones, minContractDate, maxFinalReportDate)
      }

    case MILESTONE_TYPE.ANNUAL_PLAN:
      if (processType === PROJECT.KAP) {
        return getProgramValidationForAnnualPlanning(currentMilestone, milestones, minContractDate, maxFinalReportDate)
      } else {
        return undefined
      }

    case MILESTONE_TYPE.FINANCIAL_REPORT:
      if (processType === PROJECT.KAP) {
        return getProgramValidationForFinancialReport(currentMilestone, milestones, minContractDate, maxFinalReportDate)
      } else {
        return getProjectValidationForFinancialReport(currentMilestone, milestones, minContractDate, maxFinalReportDate)
      }

    case MILESTONE_TYPE.FINAL_REPORT:
      if (processType === PROJECT.KAP) {
        return getProgramValidationForFinalReport(milestones)
      } else {
        return getProjectValidationForFinalReport(milestones)
      }

    default:
      return undefined
  }
}

const getDefaultMinAllowedDateForMilestone = (milestones: Milestone[]) => {
  const contractMilestone = milestones.find((item) => item.type === MILESTONE_TYPE.CONTRACT)

  if (!contractMilestone) {
    console.warn("Contract milestone doesn't exists")
    return undefined
  }

  return DateUtils.parseDate(DateUtils.parseAndFormatDate(contractMilestone.due_date))
}

const getDefaultMaxAllowedDateForMilestone = (milestones: Milestone[]) => {
  const finalReportMilestone = milestones.find((item) => item.type === MILESTONE_TYPE.FINAL_REPORT)

  if (!finalReportMilestone) {
    console.warn("Final report milestone doesn't exists")
    return undefined
  }

  return DateUtils.parseDate(DateUtils.parseAndFormatDate(finalReportMilestone.due_date))
}
